import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, Crown, Lock } from 'lucide-react';

interface MessageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  resetTime: Date;
}

export const MessageLimitModal: React.FC<MessageLimitModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  resetTime
}) => {
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const resetTimeMs = resetTime.getTime();
      const difference = resetTimeMs - now;

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeRemaining(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setTimeRemaining('00:00:00');
      }
    };

    if (isOpen) {
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, resetTime]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md mx-auto">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center animate-scale-in">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          
          <DialogTitle className="text-xl font-bold text-foreground">
            Free Chatting Depleted!
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          {/* Countdown Timer */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Resets in</span>
            </div>
            <div className="text-3xl font-bold text-primary font-mono bg-primary/10 rounded-lg py-3 px-6 inline-block">
              {timeRemaining}
            </div>
          </div>
          
          {/* Upgrade Message */}
          <div className="text-center space-y-4">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-foreground">Premium Benefits</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Upgrade to Premium to get unlimited chatting
              </p>
            </div>
            
            <Button 
              variant="dreamy" 
              size="lg" 
              onClick={onUpgrade}
              className="w-full animate-scale-in"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};